import { useCallback, useState } from 'react';

import {
  AccordionGroup,
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  Box,
  MixpanelEvent,
  Tab,
  TabList,
  TabPanels,
  TabSize,
  Tabs,
  beginningOfDay,
  formattedDateForSubscription,
  tabLabelerEnumerated,
  useConstant,
  useDynamicCallback,
  useMixpanel,
  usePersistedTabs,
  usePortal,
  useTabs,
  type Column,
  type PersistedDefaultTabProps,
  type TabProps,
} from '@talos/kyoko';
import { addDays } from 'date-fns';
import { useBlotterTableTabCloning } from '../../../providers/AppConfigRedux/useBlotterTableTabCloning';
import { TradeAllocationsBlotterTable } from './TradeAllocationsBlotterTable';
import { PERSISTED_TABS_ID } from './tokens';
import type { TradeAllocationFilter } from './useTradeAllocationFilter';

export * from './TradeAllocationsBlotterTable';
export { PERSISTED_TABS_ID };

export interface TradeAllocationsTabProps extends TabProps {
  defaultFilter: TradeAllocationFilter;
  defaultColumns?: Column[];
}

const PRESET_TABS: PersistedDefaultTabProps<TradeAllocationsTabProps>[] = [
  {
    label: 'Allocations',
    id: 'recent-allocations',
    closable: false,
    editable: false,
    defaultFilter: {
      StartDate: formattedDateForSubscription(addDays(beginningOfDay(), -2)),
    },
  },
];

const NEW_TAB_DEFAULTS: TradeAllocationsTabProps = {
  defaultFilter: {
    StartDate: formattedDateForSubscription(addDays(beginningOfDay(), -2)),
  },
};

const tabLabeler = tabLabelerEnumerated('Allocations');
function getBlotterID(tabID: string | undefined) {
  return `${PERSISTED_TABS_ID}/${tabID}`;
}

export function TradeAllocations() {
  const mixpanel = useMixpanel();
  const [initialFiltersOpen, setInitialFiltersOpen] = useState(false);

  const handleSelect = useCallback(() => setInitialFiltersOpen(false), []);
  const handleAdd = useDynamicCallback(() => {
    mixpanel.track(MixpanelEvent.AddTab);
    setInitialFiltersOpen(true);
  });

  const defaultInitialItems = useConstant<PersistedDefaultTabProps<TradeAllocationsTabProps>[]>(PRESET_TABS);
  const [excludedIDs] = useState(PRESET_TABS.map(tab => tab.id || ''));

  const persistedTabs = usePersistedTabs<TradeAllocationsTabProps>(PERSISTED_TABS_ID, {
    defaultInitialItems,
    defaultInitialSelectedIndex: 0,
    excludedIDs,
    onSelect: handleSelect,
  });

  const tabs = useTabs<TradeAllocationsTabProps>({
    ...persistedTabs,
    showAddTab: true,
    tabLabeler,
    onAdd: handleAdd,
    onClone: useBlotterTableTabCloning({ getBlotterID }),
  });

  const handleCloneTab = useCallback(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    tabs.clone(tabs.selectedIndex);
  }, [mixpanel, tabs]);

  const { setPortalRef: filtersContainerRef } = usePortal(BLOTTER_TABLE_FILTERS_CONTAINER_ID);

  return (
    <Tabs {...tabs} h="100%" size={TabSize.Large}>
      <TabList isBordered rightItems={<Box ref={filtersContainerRef} />}>
        {tabs.items.map((tab, idx) => (
          <Tab key={idx} {...tab} reorderable={true} />
        ))}
      </TabList>
      <TabPanels style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
        {tabs.items.map((tab, idx) => (
          <AccordionGroup key={tab.id}>
            <TradeAllocationsBlotterTable
              key={idx}
              blotterID={`${PERSISTED_TABS_ID}/${tab.id}`}
              blotterPersistID={`${PERSISTED_TABS_ID}/${tab.id}`}
              tabLabel={tab.label}
              defaultColumns={tab.defaultColumns}
              defaultFilter={tab.defaultFilter ?? NEW_TAB_DEFAULTS.defaultFilter}
              onCloneTab={handleCloneTab}
              initialIsOpen={initialFiltersOpen}
              showCloneTab={true}
            />
          </AccordionGroup>
        ))}
      </TabPanels>
    </Tabs>
  );
}
